import React, {useRef, useState, useCallback, useEffect} from 'react'
import {Box, IconButton, useToken} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getGDIHideStyles, hasGDIViewTypes, pickGDIDomProps} from '../../utils/gdi-styles'
import {getExpandedFromContentArea} from '../../utils/gdi-content-area'
import {useHeaderClearance} from '../../utils/use-header-clearance'
import GDILinkBlock from '../gdiLinkBlock'
import {Mute, Pause, Play, Unmute} from '../../../../icons'
import styles from './styles'

const hasAuthoredValue = (value) => value !== undefined && value !== null && value !== ''

const parseCmsBoolean = (value, fallback) => {
    if (!hasAuthoredValue(value)) return fallback
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value !== 0
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true
        if (['false', '0', 'no', 'off'].includes(normalized)) return false
    }

    return fallback
}

const getFirstAuthoredValue = (source, keys) => {
    for (const key of keys) {
        if (hasAuthoredValue(source?.[key])) return source[key]
    }

    return undefined
}

const resolveMediaUrl = (ref) => ref?.Url || ref?.Expanded?.Url || null

// The .vtt is cross-origin without CORS, so we proxy it same-origin (/api/caption-proxy
// in ssr.js). A <track> is a `media-src` resource, so CSP media-src must allow 'self'.
const CAPTION_PROXY_PATH = '/api/caption-proxy'

const resolveBreakpointKey = (lg, md) => {
    if (lg?.matches) return 'desktop'
    if (md?.matches) return 'tablet'
    return 'mobile'
}

const getDefaultStylesEntry = (stylesPerViewType) => {
    if (!Array.isArray(stylesPerViewType) || stylesPerViewType.length === 0) return {}
    return stylesPerViewType.find((entry) => !hasGDIViewTypes(entry)) || stylesPerViewType[0] || {}
}

const GDIVideoBlock = ({
    HideBlockOn,
    VideoExternalUrl,
    VideoExternalUrlTablet,
    VideoExternalUrlDesktop,
    AriaLabelText,
    PosterImage,
    Link,
    GDIVideoBlockLink,
    Caption,
    GDIVideoBlockStylesPerViewType,
    ...props
}) => {
    const hideStyles = getGDIHideStyles(HideBlockOn)

    const videoRef = useRef(null)

    /* Ref on the video root so `useHeaderClearance` can detect when this
     * block renders behind a transparent (overlay) header (e.g. a homepage
     * hero video). In that case the play/pause + mute controls — anchored
     * top: 16px inside the video — would otherwise be hidden under the
     * header. The hook returns an effective top offset that lands the
     * controls 24px under the header's visible bottom; returns `null` when
     * no clearance is needed so the authored default applies. */
    const rootRef = useRef(null)
    const controlsClearancePx = useHeaderClearance(rootRef)

    // Video controls are global; prefer the non-view-type default entry when present.
    const defaultStyles = getDefaultStylesEntry(GDIVideoBlockStylesPerViewType)
    const autoPlay = parseCmsBoolean(defaultStyles.AutoPlay, true)
    const loop = parseCmsBoolean(defaultStyles.Loop, true)
    const authoredMute = getFirstAuthoredValue(defaultStyles, ['MuteVideo'])
    const muteVideo = hasAuthoredValue(authoredMute)
        ? parseCmsBoolean(authoredMute, false)
        : autoPlay
    const hideMuteIcon = parseCmsBoolean(defaultStyles.HideMuteIcon, false)
    const hidePlayPauseIcon = parseCmsBoolean(
        getFirstAuthoredValue(defaultStyles, [
            'HidePlayPauseIcon',
            'GDIVideoBlockHidePlayPauseIcon'
        ]),
        false
    )

    const [isPlaying, setIsPlaying] = useState(autoPlay)
    const [isMuted, setIsMuted] = useState(muteVideo)

    const posterBlock = Array.isArray(PosterImage)
        ? getExpandedFromContentArea(PosterImage)
        : PosterImage?.Expanded || PosterImage
    const posterMobileUrl = resolveMediaUrl(posterBlock?.Image) || resolveMediaUrl(posterBlock)
    const posterTabletUrl = resolveMediaUrl(posterBlock?.ImageTablet) || posterMobileUrl
    const posterDesktopUrl = resolveMediaUrl(posterBlock?.ImageDesktop) || posterTabletUrl

    // Only the breakpoint-appropriate source is assigned (on the client), so hidden
    // breakpoints are never downloaded. Breakpoints come from the theme (useToken is
    // SSR-safe); matchMedia runs in an effect. `activeKey` is null until mounted.
    const [mdBreakpoint, lgBreakpoint] = useToken('breakpoints', ['md', 'lg'])
    const [activeKey, setActiveKey] = useState(null)
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            setActiveKey('mobile')
            return undefined
        }
        const lg = window.matchMedia(`(min-width: ${lgBreakpoint})`)
        const md = window.matchMedia(`(min-width: ${mdBreakpoint})`)
        const compute = () => setActiveKey(resolveBreakpointKey(lg, md))
        compute()
        lg.addEventListener?.('change', compute)
        md.addEventListener?.('change', compute)
        return () => {
            lg.removeEventListener?.('change', compute)
            md.removeEventListener?.('change', compute)
        }
    }, [mdBreakpoint, lgBreakpoint])

    const sources = {
        mobile: {url: VideoExternalUrl, poster: posterMobileUrl},
        tablet: {url: VideoExternalUrlTablet || VideoExternalUrl, poster: posterTabletUrl},
        desktop: {
            url: VideoExternalUrlDesktop || VideoExternalUrlTablet || VideoExternalUrl,
            poster: posterDesktopUrl
        }
    }
    const activeSource = sources[activeKey || 'mobile']

    // Caption .vtt routed through the same-origin proxy (see CAPTION_PROXY_PATH note).
    const captionUrl = Caption?.Url || Caption?.Expanded?.Url || null
    const captionSrc = captionUrl
        ? `${CAPTION_PROXY_PATH}?url=${encodeURIComponent(captionUrl)}`
        : null

    // Keep the muted property in sync (React's `muted` attr isn't reliably reflected).
    useEffect(() => {
        if (videoRef.current) videoRef.current.muted = isMuted
    }, [isMuted])

    // Autoplay the active source once its src is assigned on the client.
    useEffect(() => {
        const video = videoRef.current
        if (!activeKey || !autoPlay || !video) return
        // Ignore rejections — a blocked autoplay is covered by the poster + `autoPlay` attr.
        video.play?.()?.catch?.(() => {
            /* autoplay blocked */
        })
    }, [activeKey, autoPlay, activeSource.url])

    const togglePlayPause = useCallback((event) => {
        event?.preventDefault()
        event?.stopPropagation()

        const video = videoRef.current
        if (!video) return
        if (video.paused) {
            const playPromise = video.play()
            setIsPlaying(true)
            playPromise?.catch?.(() => setIsPlaying(false))
        } else {
            video.pause()
            setIsPlaying(false)
        }
    }, [])

    const toggleMute = useCallback(
        (event) => {
            event?.preventDefault()
            event?.stopPropagation()

            const nextMuted = !isMuted
            if (videoRef.current) videoRef.current.muted = nextMuted
            setIsMuted(nextMuted)
        },
        [isMuted]
    )

    if (hideStyles.doNotLoadComponent) return null
    if (!VideoExternalUrl) return null

    const linkBlock = getExpandedFromContentArea(GDIVideoBlockLink || Link)

    const videoElement = (
        <Box
            ref={rootRef}
            data-block-type="GDIVideoBlock"
            sx={hideStyles.sx}
            {...styles.root}
            {...pickGDIDomProps(props)}
        >
            {/* Single video — only the breakpoint-appropriate source is loaded */}
            <Box {...styles.responsiveVideoWrapper}>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption -- caption <track> is rendered below when a .vtt is authored in the CMS */}
                <video
                    ref={videoRef}
                    src={activeKey ? activeSource.url : undefined}
                    poster={activeSource.poster}
                    autoPlay={autoPlay}
                    loop={loop}
                    muted={isMuted}
                    playsInline
                    preload={activeKey ? 'auto' : 'none'}
                    aria-label={AriaLabelText}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    style={styles.coverVideo}
                >
                    {captionSrc && (
                        <track
                            kind="captions"
                            srcLang="en"
                            label="Captions"
                            src={captionSrc}
                            default
                        />
                    )}
                </video>
            </Box>

            {(!hidePlayPauseIcon || !hideMuteIcon) && (
                <Box
                    {...styles.controls}
                    {...(controlsClearancePx != null ? {top: `${controlsClearancePx}px`} : null)}
                >
                    {!hidePlayPauseIcon && (
                        <IconButton
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                            onClick={togglePlayPause}
                            {...styles.controlButton}
                            icon={isPlaying ? <Pause /> : <Play />}
                        />
                    )}

                    {!hideMuteIcon && (
                        <IconButton
                            aria-label={isMuted ? 'Unmute' : 'Mute'}
                            onClick={toggleMute}
                            {...styles.controlButton}
                            icon={isMuted ? <Unmute /> : <Mute />}
                        />
                    )}
                </Box>
            )}
        </Box>
    )

    if (linkBlock) {
        return <GDILinkBlock {...linkBlock}>{videoElement}</GDILinkBlock>
    }

    return videoElement
}

GDIVideoBlock.propTypes = {
    HideBlockOn: PropTypes.string,
    VideoExternalUrl: PropTypes.string,
    VideoExternalUrlTablet: PropTypes.string,
    VideoExternalUrlDesktop: PropTypes.string,
    AriaLabelText: PropTypes.string,
    PosterImage: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.shape({Expanded: PropTypes.object})
    ]),
    Link: PropTypes.array,
    GDIVideoBlockLink: PropTypes.array,
    Caption: PropTypes.shape({Url: PropTypes.string, Expanded: PropTypes.object}),
    GDIVideoBlockStylesPerViewType: PropTypes.array,
    GDIVideoBlockAudienceTargeting: PropTypes.object
}

export default GDIVideoBlock
